/**
 * baseline-review-brief.ts — editorial copy layer for the FIRST-RUN welcome
 * brief (WO-9, reworked 2026-07-10 from the original bless-gate ceremony).
 *
 * A born-Hybrid tenant's baseline is the monitor snapshot Guardian took at
 * onboarding — nothing gates on customer approval. On first visit the drift
 * page shows a one-time "here's your starting point" brief; the single CTA
 * opens the (already live) queue and records an acknowledgment server-side.
 * This composer supplies the brief's standfirst: headline, lede, six-word
 * verdict, and byline. The customer's first impression of Guardian
 * runs through here, so the voice is "trusted advisor": name the verdict before
 * the data, lead with named subjects, no em-dashes in headlines.
 *
 * The copy is DELTA-framed, deliberately: the brief lists what changed SINCE
 * the baseline snapshot, never claiming those rows are "your configuration" or
 * that anything awaits approval (the original ceremony's copy oversold a
 * two-item delta as a whole-tenant baseline blessing).
 *
 * Modeled on `posture-brief.ts`: every editorial slot is ONE deterministic,
 * data-driven line. There is no variant table and no rotation — the same
 * input always produces the same brief, and all variation in what the user
 * reads comes from the real data (item count, flagged-item count),
 * never from picking among synonymous phrasings of the same fact.
 * Same six-word verdict budget, same refuse-to-fabricate discipline:
 *
 *   - There is NO "N% healthy" score. No impact model exists on day 1
 *     (`posture-pages-supplements.md` fabrication rule), so the brief never
 *     synthesises one. Every number it speaks — item count and flagged
 *     items — is a real field passed in by the caller (item totals and
 *     Critical/High severity counts from the loaded delta rows).
 *   - When the item count is null (the delta query hasn't resolved) the brief
 *     returns a neutral "Reading your configuration…" state rather than
 *     coercing null → 0 and claiming a settled start before the data lands.
 *
 * Action-first fields (blueprint §6.2 row 10): `headline` names the flagged
 * changes to review first, else that nothing needs review; `lede` states the
 * baseline fact and the change count. `primaryAction` carries `ctaLabel` and
 * is set only when the caller passes `ctaHref`: the welcome's CTA also
 * records the acknowledgment, so a page that needs that mutation keeps its
 * own button instead of a link.
 */

import type { ActionBriefFields } from './brief-shape'
import { briefProvenance } from './brief-helpers'

export interface BaselineReviewBrief {
  /** Six-words-or-fewer verdict. Frames the starting point; it is NOT a score.
   *  Not drawn: the welcome brief shows no verdict (D22). */
  verdict: string
  /** The provenance line (D36): "Assessed by Guardian at 06:12 today", or "Awaiting first assessment". */
  byline: string
  /** Primary CTA label the brief renders. Constant, exported for reuse. */
  ctaLabel: string
  /** Action-first h1 (blueprint §6.2 row 10). */
  headline: string
  /** Never set by this composer. */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
  /** `ctaLabel` as the brief's one primary action; only when `ctaHref` is passed. */
  primaryAction?: ActionBriefFields['primaryAction']
}

export interface BaselineReviewBriefInput {
  /** The tenant's last real assessment (`lastAssessmentAt`), for the provenance
   *  line. Null reads "Awaiting first assessment"; undefined draws no line. */
  assessedAt?: string | null
  /** The clock for "today" / "yesterday" in the provenance line; tests pin it. */
  now?: Date
  /** Active tenant id. Unused by the composer — copy is deterministic from
   *  the item data alone. Kept so callers don't need to change
   *  their call sites. */
  tenantId?: string
  /**
   * Total items changed since the baseline snapshot. `null`/`undefined` means
   * the delta query hasn't resolved yet → neutral loading brief (no coercion to 0).
   */
  itemCount?: number | null
  /**
   * Count of Critical + High severity items in the delta, flagged for early
   * attention. Real (severity is on every row). Defaults to 0 when absent.
   */
  worthALook?: number
  /**
   * Where the primary action goes. When set, the brief carries
   * `primaryAction: { label: ctaLabel, href: ctaHref }` for BriefHero. Omit it
   * when the page renders its own CTA (the acknowledge mutation).
   */
  ctaHref?: string
}

/* -------------------------------------------------------------------------- */
/* Exported copy constants (shared with the welcome component + tests)         */
/* -------------------------------------------------------------------------- */

/** The single primary CTA. One verb, plain language: the queue is already
 *  live, opening it is all there is to do. Named here so the component and its
 *  tests never drift apart. */
export const BASELINE_REVIEW_CTA = 'Open your drift queue'

/* -------------------------------------------------------------------------- */
/* Helpers (mirrored from posture-brief.ts — kept local so this module stays  */
/* self-contained)                                                            */
/* -------------------------------------------------------------------------- */

/** Small-number word form for honest counts inside prose; digits above ten. */
function numberWord(n: number): string {
  const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
  return words[n] ?? String(n)
}

function cap(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  LOADING: 'Reading your configuration…',
  ZERO: 'Nothing has changed since baseline.',
  CLEAN: 'Nothing to review before you start.',
  FLAGGED_ONE: 'One change to review first.',
  FLAGGED: '{W} changes to review first.',
}

/** Action-first h1 (blueprint §6.2 row 10): the flagged changes first. */
const HEADLINE_ACTION = {
  LOADING: 'Reading your configuration.',
  FLAGGED_ONE: 'Review the flagged change first.',
  FLAGGED: 'Review the {w} flagged changes first.',
  CLEAN: 'Nothing to review before you start.',
}

/** The lede: the baseline fact, then the change count since the snapshot. */
const LEDE = {
  LOADING: '**Guardian is reading your configuration**.',
  ZERO: '**Your baseline is set**, and nothing has changed since.',
  ONE: '**Your baseline is set**, with 1 change since.',
  FEW: '**Your baseline is set**, with {n} changes since.',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six-words-or-fewer)                                              */
/* -------------------------------------------------------------------------- */

function pickVerdict(itemCount: number, worthALook: number): string {
  if (itemCount === 0) return VERDICT.ZERO
  if (worthALook === 0) return VERDICT.CLEAN
  if (worthALook === 1) return VERDICT.FLAGGED_ONE
  return VERDICT.FLAGGED.replace('{W}', cap(numberWord(worthALook)))
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 10)                       */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(worthALook: number): string {
  if (worthALook === 1) return HEADLINE_ACTION.FLAGGED_ONE
  if (worthALook >= 2) return HEADLINE_ACTION.FLAGGED.replace('{w}', String(worthALook))
  return HEADLINE_ACTION.CLEAN
}

function buildLede(itemCount: number): string {
  if (itemCount === 0) return LEDE.ZERO
  if (itemCount === 1) return LEDE.ONE
  return LEDE.FEW.replace('{n}', String(itemCount))
}

/** `{ label: ctaLabel, href }` when the caller gave a non-empty href. */
function primaryActionFor(ctaHref: string | undefined): ActionBriefFields['primaryAction'] | undefined {
  const href = ctaHref?.trim()
  return href ? { label: BASELINE_REVIEW_CTA, href } : undefined
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose the first-run welcome brief. All inputs optional; the function
 * degrades gracefully toward a neutral "reading" brief rather than throwing or
 * fabricating.
 *
 * `itemCount == null` (the delta query hasn't resolved) returns the neutral
 * loading brief. `itemCount === 0` is a legitimate value (a spotless
 * born-Hybrid tenant) and routes through the empty-queue path.
 */
export function composeBaselineReviewBrief(
  input: BaselineReviewBriefInput = {},
): BaselineReviewBrief {
  // The provenance line (D36). No scope clause: the lede names the changes.
  const byline = briefProvenance(input.assessedAt, { now: input.now })

  const primaryAction = primaryActionFor(input.ctaHref)

  if (input.itemCount == null) {
    return {
      verdict: VERDICT.LOADING,
      byline,
      ctaLabel: BASELINE_REVIEW_CTA,
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
      ...(primaryAction ? { primaryAction } : {}),
    }
  }

  const itemCount = Math.max(0, input.itemCount)
  // The flagged-item count can never honestly exceed the total item count;
  // clamp so a caller passing an over-count can't drive a nonsense verdict.
  const worthALook = Math.max(0, Math.min(input.worthALook ?? 0, itemCount))

  const verdict = pickVerdict(itemCount, worthALook)

  return {
    verdict,
    byline,
    ctaLabel: BASELINE_REVIEW_CTA,
    headline: pickActionHeadline(worthALook),
    lede: buildLede(itemCount),
    ...(primaryAction ? { primaryAction } : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
}
